<script setup lang="ts">
/**
 * UpdateTab — settings tab providing in-panel self-update (v3.19.6).
 *
 * Before this, updating AFD meant SSH-ing into the box and running
 * `afd update`. That was fine for one machine and awful for five.
 *
 * The confirm flow deliberately mirrors DangerZoneTab (countdown + typed
 * token), because the consequence here is the same class of operation:
 * replacing the running binary on the server is equivalent to granting
 * code execution. The 5s arming delay and the literal token exist so a
 * stray click cannot swap the binary out from under an operator.
 *
 * The service restarts itself after a successful apply — the page will
 * drop its connection for a few seconds. That is expected, not a failure;
 * the UI says so explicitly and re-checks on a timer.
 */
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { AlertTriangle, Download, RefreshCw } from 'lucide-vue-next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import Card from '@/components/ui/Card.vue'
import Button from '@/components/ui/Button.vue'
import Modal from '@/components/ui/Modal.vue'
import Spinner from '@/components/ui/Spinner.vue'
import { applyUpdate, checkUpdate } from '@/api/update'
import { useToast } from '@/composables/useToast'

// 后端 apply 要求 confirm 字段与此常量一致
const UPDATE_CONFIRM_TOKEN = 'upgrade'
// 与 DangerZoneTab 一致: 5 秒后才允许确认, 挡住手滑
const ARM_DELAY_MS = 5000
// 升级后服务要重启, 留一段宽限期再复查版本
const RECHECK_AFTER_MS = 8000

const { t } = useI18n()
const toast = useToast()
const qc = useQueryClient()

const showConfirm = ref(false)
const countdown = ref(ARM_DELAY_MS / 1000)
const confirmText = ref('')
// v3.19.57/58: 界面构建标识 + 独立版本号 (vite define 注入) — 模板作用域
// 解析不到全局 declare const, 在 script 里转交一次。
const uiBuild = __UI_BUILD__
const uiVersion = __UI_VERSION__
let countdownTimer: ReturnType<typeof setInterval> | null = null
// 服务重启期间用来提示用户"这是预期行为"
const rebooting = ref(false)
let recheckTimer: ReturnType<typeof setTimeout> | null = null

const checkQuery = useQuery({
  queryKey: ['update', 'check'],
  queryFn: checkUpdate,
  // 版本检查走网络, 不必频繁; 60s 足够
  refetchInterval: 60_000,
})

const info = computed(() => checkQuery.data.value)
const canConfirm = computed(
  () => confirmText.value.trim() === UPDATE_CONFIRM_TOKEN && countdown.value <= 0,
)

const applyMutation = useMutation({
  mutationFn: applyUpdate,
  onSuccess: (resp) => {
    showConfirm.value = false
    clearCountdown()
    rebooting.value = true
    toast.success(resp.message)
    void qc.invalidateQueries({ queryKey: ['update'] })
    // 服务会自己重启, 连接会断几秒 — 到点复查而不是立刻查
    if (recheckTimer !== null) clearTimeout(recheckTimer)
    recheckTimer = setTimeout(() => {
      rebooting.value = false
      void qc.invalidateQueries({ queryKey: ['update'] })
    }, RECHECK_AFTER_MS)
  },
  onError: (err: Error) => {
    showConfirm.value = false
    clearCountdown()
    toast.error(err.message, 'AFD Console')
  },
})

function clearCountdown(): void {
  if (countdownTimer !== null) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

function openConfirm(): void {
  confirmText.value = ''
  countdown.value = ARM_DELAY_MS / 1000
  showConfirm.value = true
  countdownTimer = setInterval(() => {
    countdown.value -= 1
    if (countdown.value <= 0) {
      clearCountdown()
    }
  }, 1000)
}

function closeConfirm(): void {
  showConfirm.value = false
  clearCountdown()
  confirmText.value = ''
}

function confirmUpgrade(): void {
  if (!canConfirm.value) return
  applyMutation.mutate()
}

onBeforeUnmount(() => {
  clearCountdown()
  if (recheckTimer !== null) clearTimeout(recheckTimer)
})
</script>

<template>
  <div class="space-y-4">
    <!-- 当前版本 -->
    <Card>
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 class="text-sm font-medium text-fg">{{ t('settings.update.currentVersion') }}</h3>
          <p class="mt-1 text-xs text-fg-muted">
            {{ t('settings.update.installedOn', { version: info?.current_version ?? '…' }) }}
            <span class="font-mono">· UI {{ uiVersion }} ({{ uiBuild }})</span>
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          :loading="checkQuery.isFetching.value"
          @click="void qc.invalidateQueries({ queryKey: ['update'] })"
        >
          <RefreshCw :size="14" />
          {{ t('settings.update.check') }}
        </Button>
      </div>
    </Card>

    <!-- 检查结果 -->
    <Card v-if="info">
      <!-- 已是最新 -->
      <div v-if="!info.error && !info.upgradable" class="flex items-center gap-2 text-sm text-success">
        <span>✓</span>
        <span>{{ t('settings.update.upToDate') }}</span>
      </div>

      <!-- 可升级 -->
      <div v-else-if="!info.error && info.upgradable" class="space-y-3">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-baseline gap-2">
            <span class="font-mono text-sm text-fg-muted">{{ info.current_version }}</span>
            <span class="text-fg-subtle">→</span>
            <span class="font-mono text-sm font-semibold text-success">{{ info.latest_tag }}</span>
          </div>
          <Button variant="primary" size="sm" @click="openConfirm">
            <Download :size="14" />
            {{ t('settings.update.upgrade') }}
          </Button>
        </div>

        <!-- 更新日志 -->
        <details v-if="info.notes" class="rounded-lg border border-border bg-muted px-3 py-2">
          <summary class="cursor-pointer text-xs font-medium text-fg-muted">
            {{ t('settings.update.releaseNotes') }}
          </summary>
          <pre class="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-xs text-fg">{{ info.notes }}</pre>
        </details>
      </div>

      <!-- 检查失败 -->
      <div v-else-if="info.error" class="flex items-start gap-2 text-sm text-danger">
        <AlertTriangle :size="16" class="mt-0.5 shrink-0" />
        <span>{{ info.error }}</span>
      </div>
    </Card>

    <!-- 重启提示 -->
    <Card v-if="rebooting" class="border-info/40 bg-info/5">
      <div class="flex items-center gap-2 text-sm text-fg-muted">
        <Spinner :size="14" />
        <span>{{ t('settings.update.rebooting') }}</span>
      </div>
    </Card>

    <!-- 确认弹窗 -->
    <Modal v-model="showConfirm" :title="t('settings.update.confirmTitle')" size="md" @close="closeConfirm">
      <div class="space-y-4">
        <div class="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2">
          <p class="text-xs font-medium text-fg">{{ t('settings.update.confirmImpactTitle') }}</p>
          <ul class="mt-1 list-inside list-disc space-y-0.5 text-xs text-fg-muted">
            <li>{{ t('settings.update.impactReplace') }}</li>
            <li>{{ t('settings.update.impactRestart') }}</li>
            <li>{{ t('settings.update.impactDownload') }}</li>
          </ul>
        </div>

        <p class="text-xs text-fg-muted">
          {{ t('settings.update.typeToConfirm', { token: UPDATE_CONFIRM_TOKEN }) }}
        </p>
        <input
          v-model="confirmText"
          :placeholder="UPDATE_CONFIRM_TOKEN"
          class="w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm text-fg outline-none focus:border-primary"
          autocomplete="off"
        />
      </div>
      <template #footer>
        <Button variant="ghost" size="sm" @click="closeConfirm">{{ t('common.cancel') }}</Button>
        <Button
          variant="danger"
          size="sm"
          :disabled="!canConfirm"
          :loading="applyMutation.isPending.value"
          @click="confirmUpgrade"
        >
          {{ countdown > 0 ? `${t('settings.update.confirmIn')} ${countdown}s` : t('settings.update.confirmUpgrade') }}
        </Button>
      </template>
    </Modal>
  </div>
</template>
